/**
 * Service d'onboarding progressif
 * Gère la collecte progressive des informations utilisateur
 */

const ONBOARDING_KEY = 'yiatou_onboarding';

// Définition des étapes d'onboarding
const ONBOARDING_STEPS = {
  PROFILE_NAME: {
    id: 'profile_name',
    title: 'Complète ton profil',
    description: 'Comment tu t\'appelles ?',
    fields: ['first_name', 'last_name'],
    triggerAfter: ['first_order', 'first_login'], // Déclenche après première commande ou première connexion
    priority: 1,
  },
  ADDRESS: {
    id: 'address',
    title: 'Adresse de livraison',
    description: 'Ajoute ton adresse pour faciliter tes livraisons',
    fields: ['address'],
    triggerAfter: ['add_to_cart', 'checkout'], // Déclenche lors de l'ajout au panier ou checkout
    priority: 2,
  },
  MOBILE_MONEY: {
    id: 'mobile_money',
    title: 'Numéro Mobile Money',
    description: 'Ajoute au moins un numéro pour tes paiements',
    fields: ['mobile_money_phone', 'moov_money_phone'],
    // Deux opérateurs, un seul suffit : un client n'a pas forcément les
    // deux comptes. `getNextStep`/la validation du formulaire lisent ce
    // marqueur pour exiger « au moins un » plutôt que « tous ».
    requireAny: true,
    triggerAfter: ['payment'], // Déclenche lors du paiement
    priority: 3,
  },
};

class OnboardingService {
  constructor() {
    this.state = this.loadState();
  }

  // Charger l'état depuis localStorage
  loadState() {
    try {
      const stored = localStorage.getItem(ONBOARDING_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Erreur chargement onboarding state:', e);
    }
    return {
      completedSteps: [],
      skippedSteps: [],
      dismissedUntil: {}, // { stepId: timestamp }
      actionCount: {}, // { action: count }
    };
  }

  // Sauvegarder l'état
  saveState() {
    try {
      localStorage.setItem(ONBOARDING_KEY, JSON.stringify(this.state));
    } catch (e) {
      console.warn('Erreur sauvegarde onboarding state:', e);
    }
  }

  // Vérifier si un champ est manquant dans le profil utilisateur
  isFieldMissing(user, field) {
    if (!user) return true;
    const value = user[field] || user[this.snakeToCamel(field)];
    return !value || value === 'null' || value === 'Non renseignée';
  }

  // Convertir snake_case en camelCase
  snakeToCamel(str) {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  }

  // Enregistrer une action utilisateur
  trackAction(action) {
    if (!this.state.actionCount[action]) {
      this.state.actionCount[action] = 0;
    }
    this.state.actionCount[action]++;
    this.saveState();
  }

  // Obtenir la prochaine étape d'onboarding à afficher
  getNextStep(user, currentAction = null) {
    // Trier les étapes par priorité
    const sortedSteps = Object.values(ONBOARDING_STEPS).sort(
      (a, b) => a.priority - b.priority
    );

    for (const step of sortedSteps) {
      // Vérifier si l'étape est déjà complétée
      if (this.state.completedSteps.includes(step.id)) {
        continue;
      }

      // Vérifier si l'étape est temporairement ignorée
      const dismissedUntil = this.state.dismissedUntil[step.id];
      if (dismissedUntil && Date.now() < dismissedUntil) {
        continue;
      }

      // Vérifier si les champs sont déjà renseignés — « au moins un » pour
      // une étape marquée `requireAny` (ex. Mobile Money : Airtel ou Moov
      // suffit), « tous » sinon.
      const allFieldsFilled = step.requireAny
        ? step.fields.some((field) => !this.isFieldMissing(user, field))
        : step.fields.every((field) => !this.isFieldMissing(user, field));
      if (allFieldsFilled) {
        this.markCompleted(step.id);
        continue;
      }

      // Vérifier si l'action déclenche cette étape
      if (currentAction && step.triggerAfter.includes(currentAction)) {
        return step;
      }
    }

    return null;
  }

  /**
   * Le client a-t-il écarté cette étape récemment ?
   *
   * C'est le seul état local qui puisse retenir une sollicitation. Savoir si
   * une information est renseignée se lit sur le compte, pas ici : cet état vit
   * dans le navigateur et ne distingue pas les comptes qui s'y succèdent.
   */
  isDismissed(stepId) {
    const dismissedUntil = this.state.dismissedUntil[stepId];

    return !!dismissedUntil && Date.now() < dismissedUntil;
  }

  // Marquer une étape comme complétée
  markCompleted(stepId) {
    if (!this.state.completedSteps.includes(stepId)) {
      this.state.completedSteps.push(stepId);
      this.saveState();
    }
  }

  // Ignorer une étape temporairement (24h par défaut)
  dismissStep(stepId, hours = 24) {
    this.state.dismissedUntil[stepId] = Date.now() + hours * 60 * 60 * 1000;
    this.saveState();
  }

  // Ignorer définitivement une étape
  skipStep(stepId) {
    if (!this.state.skippedSteps.includes(stepId)) {
      this.state.skippedSteps.push(stepId);
      this.saveState();
    }
  }

  // Un numéro Mobile Money suffit, quel que soit l'opérateur — Airtel et
  // Moov comptent comme une seule information manquante ou renseignée,
  // jamais deux.
  hasMobileMoney(user) {
    return !this.isFieldMissing(user, 'mobile_money_phone') || !this.isFieldMissing(user, 'moov_money_phone');
  }

  // Calculer le pourcentage de complétion du profil
  getProfileCompletion(user) {
    if (!user) return 0;

    const simpleFields = ['first_name', 'last_name', 'email', 'phone', 'address'];
    const filledSimple = simpleFields.filter((field) => !this.isFieldMissing(user, field)).length;
    const total = simpleFields.length + 1;

    return Math.round(((filledSimple + (this.hasMobileMoney(user) ? 1 : 0)) / total) * 100);
  }

  // Obtenir les champs manquants
  getMissingFields(user) {
    if (!user) return [];

    const simpleFields = [
      { key: 'first_name', label: 'Prénom' },
      { key: 'last_name', label: 'Nom' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Téléphone' },
      { key: 'address', label: 'Adresse' },
    ];

    const missing = simpleFields.filter((field) => this.isFieldMissing(user, field.key));

    if (!this.hasMobileMoney(user)) {
      missing.push({ key: 'mobile_money_phone', label: 'Mobile Money' });
    }

    return missing;
  }

  // Réinitialiser l'onboarding (pour les tests)
  reset() {
    this.state = {
      completedSteps: [],
      skippedSteps: [],
      dismissedUntil: {},
      actionCount: {},
    };
    this.saveState();
  }
}

export const onboardingSteps = ONBOARDING_STEPS;
const onboardingServiceInstance = new OnboardingService();
export default onboardingServiceInstance;
